import { peekFitnessGuidance } from '@/i18n/translations';
import { getMachineExerciseGuidance } from '@/lib/fitnessMachineExerciseGuidance';
import { getAdvancedExerciseGuidance } from '@/lib/fitnessAdvancedExerciseGuidance';
import { getArmExerciseGuidance } from '@/lib/fitnessArmExerciseGuidance';
import { getCardioExerciseGuidance } from '@/lib/fitnessCardioExerciseGuidance';
import { getLowerExerciseGuidance } from '@/lib/fitnessLowerExerciseGuidance';
import { getUpperExerciseGuidance } from '@/lib/fitnessUpperExerciseGuidance';
import { getExtendedExerciseGuidance } from '@/lib/fitnessExtendedExerciseGuidance';
import { canonicalFitnessExerciseName } from "@/lib/fitnessExerciseIdentity";
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/**
 * Original VYV editorial draft, not a professional biomechanical review or
 * personalized assessment. No camera observation, repetition counting or
 * claims about the user's current posture are inferred from this content.
 *
 * Primary reference material consulted for the general movement principles:
 * https://www.nasm.org/resource-center/exercise-library
 * https://www.nasm.org/resource-center/exercise-library/push-up
 * https://www.nasm.org/resource-center/exercise-library/barbell-bench-press
 * https://www.nasm.org/resource-center/exercise-library/romanian-deadlift-barbell
 * https://www.nasm.org/resource-center/exercise-library/lying-leg-curl
 * https://www.nasm.org/workout-exercise-guidance/how-to-jumping-jacks
 *
 * Wording and translations are authored for VYV; no competitor copy or media
 * is included. Content lives in locale modules, independently of media coverage.
 */
export type ExerciseGuidanceKey =
  | 'pushUp' | 'pullUp' | 'squat' | 'deadlift' | 'benchPress'
  | 'overheadPress' | 'barbellRow' | 'dumbbellCurl' | 'tricepDip'
  | 'lateralRaise' | 'legPress' | 'lunges' | 'plank' | 'crunches'
  | 'romanianDeadlift' | 'calfRaise' | 'facePull' | 'dumbbellFly'
  | 'legCurl' | 'cableCrunch' | 'jumpingJack';

type GuidanceCopy = readonly [string, string, string, string, string];
export type ExerciseGuidanceDictionary = Record<ExerciseGuidanceKey, GuidanceCopy>;

export interface ExerciseGuidance {
  steps: string[];
  cues: string[];
}


// Deliberately small exact alias lists. Do not use substring/fuzzy matching:
// e.g. a seated curl or an incline press needs its own movement instructions.
// Catalog IDs are not aliases: a remote item called s1 may be a different move.
const aliases: Record<ExerciseGuidanceKey, readonly string[]> = {
  pushUp: ['push up', 'push ups', 'pushup', 'pushups', 'flexão', 'flexões', 'flexão de braços', 'flexões de braços'],
  pullUp: ['pull up', 'pull ups', 'pullup', 'pullups', 'barra fixa'],
  squat: ['squat', 'squats', 'bodyweight squat', 'body weight squat', 'agachamento', 'agachamento livre'],
  deadlift: ['deadlift', 'deadlifts', 'barbell deadlift', 'levantamento terra'],
  benchPress: ['bench press', 'barbell bench press', 'flat barbell bench press', 'supino reto', 'supino reto com barra'],
  overheadPress: ['overhead press', 'barbell overhead press', 'standing barbell overhead press', 'desenvolvimento com barra'],
  barbellRow: ['barbell row', 'barbell rows', 'bent over barbell row', 'remada com barra', 'remada curvada com barra'],
  dumbbellCurl: ['dumbbell curl', 'dumbbell curls', 'dumbbell biceps curl', 'rosca com haltere', 'rosca com halteres'],
  tricepDip: ['tricep dip', 'triceps dip', 'tricep dips', 'triceps dips', 'parallel bar dip', 'parallel bar dips', 'mergulho nas paralelas'],
  lateralRaise: ['lateral raise', 'lateral raises', 'dumbbell lateral raise', 'dumbbell lateral raises', 'elevação lateral', 'elevação lateral com halteres'],
  legPress: ['leg press'],
  lunges: ['lunge', 'lunges', 'forward lunge', 'forward lunges', 'avanço', 'avanços', 'afundo à frente'],
  plank: ['plank', 'forearm plank', 'prancha', 'prancha nos antebraços'],
  crunches: ['crunch', 'crunches', 'abdominal curto'],
  romanianDeadlift: ['romanian deadlift', 'barbell romanian deadlift', 'romanian deadlift barbell', 'levantamento terra romeno'],
  calfRaise: ['calf raise', 'calf raises', 'standing calf raise', 'standing calf raises', 'elevação de panturrilha'],
  facePull: ['face pull', 'face pulls'],
  dumbbellFly: ['dumbbell fly', 'dumbbell flyes', 'dumbbell flies', 'flat dumbbell fly', 'crucifixo com haltere', 'crucifixo com halteres'],
  legCurl: ['leg curl', 'leg curls'],
  cableCrunch: ['cable crunch', 'cable crunches', 'kneeling cable crunch', 'abdominal no cabo'],
  jumpingJack: ['jumping jack', 'jumping jacks', 'jumpingjack', 'jumpingjacks', 'polichinelo', 'polichinelos'],
};

function normalizeExerciseName(value: string): string {
  return value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

const keyByAlias = new Map<string, ExerciseGuidanceKey>();
for (const key of Object.keys(aliases) as ExerciseGuidanceKey[]) {
  for (const alias of aliases[key]) keyByAlias.set(normalizeExerciseName(alias), key);
}

/**
 * Resolves only a known canonical name or explicit alias. The caller must pass
 * the actual movement name or an explicit app-owned identity key. Provider
 * catalog IDs and translated display labels are never inferred.
 * Unknown names return null so their original instructions can remain visible.
 * App codes and browser locale tags share locale.ts normalization; unknown
 * languages (including missing test/mock values) explicitly fall back to EN.
 * Guidance is synchronous after loadLocale; a cold locale returns null.
 * Returned arrays are copies so one workout cannot mutate another's guidance.
 */
export function getExerciseGuidance(exerciseName: string, lang?: string): ExerciseGuidance | null {
  if (typeof exerciseName !== 'string') return null;
  const key = keyByAlias.get(normalizeExerciseName(canonicalFitnessExerciseName(exerciseName)));
  if (!key) return getMachineExerciseGuidance(exerciseName, lang) ?? getAdvancedExerciseGuidance(exerciseName, lang) ?? getArmExerciseGuidance(exerciseName, lang) ?? getCardioExerciseGuidance(exerciseName, lang) ?? getExtendedExerciseGuidance(exerciseName, lang) ?? getUpperExerciseGuidance(exerciseName, lang) ?? getLowerExerciseGuidance(exerciseName, lang);
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'base', key);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
